/**
 * 後台管理系統 - 儀表板
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { OrderStatusManager } from '@/components/admin/OrderStatusManager';
import { 
  ShoppingBag, Package, TrendingUp, Users,
  CheckCircle, Clock, XCircle, Eye, RefreshCw
} from 'lucide-react';

export function AdminDashboard() {
  const navigate = useNavigate();
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    paidOrders: 0,
    totalRevenue: 0,
  });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (!isAdmin()) {
        navigate('/member');
      }
    }
  }, [user, authLoading, isAdmin, navigate]);

  useEffect(() => {
    if (user && isAdmin()) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      // 查詢所有訂單
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false });

      if (ordersError) throw ordersError;

      setOrders(ordersData || []);

      // 計算統計數據
      const totalOrders = ordersData?.length || 0;
      const pendingOrders = ordersData?.filter(o => o.payment_status === 'pending').length || 0;
      const paidOrders = ordersData?.filter(o => o.payment_status === 'paid').length || 0;
      const totalRevenue = ordersData
        ?.filter(o => o.payment_status === 'paid')
        .reduce((sum, o) => sum + o.total_amount, 0) || 0;

      setStats({
        totalOrders,
        pendingOrders,
        paidOrders,
        totalRevenue,
      });
    } catch (error) {
      console.error('查詢數據失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-600" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-600" />;
      default:
        return <Package className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      pending: '待付款',
      paid: '已付款',
      failed: '付款失敗',
      confirmed: '已確認',
      processing: '處理中',
      shipped: '已出貨',
      delivered: '已送達',
      cancelled: '已取消',
    };
    return statusMap[status] || status;
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50 py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* 頁面標題 */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">後台管理系統</h1>
          <p className="text-gray-600">管理訂單、產品和銷售數據</p>
        </div>

        {/* 快速導航 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Button
            variant="outline"
            className="h-20 justify-start text-left"
            onClick={() => navigate('/admin/orders')}
          >
            <ShoppingBag className="h-8 w-8 mr-4 text-blue-600" />
            <div>
              <p className="font-semibold">訂單管理</p>
              <p className="text-sm text-gray-500">查看和管理所有訂單</p>
            </div>
          </Button>

          <Button
            variant="outline"
            className="h-20 justify-start text-left"
            onClick={() => navigate('/admin/members')}
          >
            <Users className="h-8 w-8 mr-4 text-purple-600" />
            <div>
              <p className="font-semibold">會員管理</p>
              <p className="text-sm text-gray-500">查看所有註冊會員</p>
            </div>
          </Button>

          <Button
            variant="outline"
            className="h-20 justify-start text-left"
            onClick={() => navigate('/admin/products')}
          >
            <Package className="h-8 w-8 mr-4 text-green-600" />
            <div>
              <p className="font-semibold">商品管理</p>
              <p className="text-sm text-gray-500">管理商品庫存資訊</p>
            </div>
          </Button>
        </div>

        {/* 統計卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0 shadow-lg">
            <CardHeader className="pb-2">
              <CardDescription className="text-blue-100">總訂單數</CardDescription>
              <CardTitle className="text-3xl">{stats.totalOrders}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-blue-100">
                <ShoppingBag className="h-4 w-4" />
                <span className="text-sm">所有訂單</span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0 shadow-lg">
            <CardHeader className="pb-2">
              <CardDescription className="text-green-100">已付款訂單</CardDescription>
              <CardTitle className="text-3xl">{stats.paidOrders}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-green-100">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm">完成付款</span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white border-0 shadow-lg">
            <CardHeader className="pb-2">
              <CardDescription className="text-yellow-100">待付款訂單</CardDescription>
              <CardTitle className="text-3xl">{stats.pendingOrders}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-yellow-100">
                <Clock className="h-4 w-4" />
                <span className="text-sm">等待付款</span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0 shadow-lg">
            <CardHeader className="pb-2">
              <CardDescription className="text-purple-100">總營收</CardDescription>
              <CardTitle className="text-3xl">
                ${(stats.totalRevenue / 1000).toFixed(1)}K
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-purple-100">
                <TrendingUp className="h-4 w-4" />
                <span className="text-sm">NT$ {stats.totalRevenue.toLocaleString()}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* 訂單列表 */}
        <Card className="bg-white/80 backdrop-blur-xl border-white/20 shadow-lg">
          <CardHeader>
            <CardTitle>最近訂單</CardTitle>
            <CardDescription>查看和管理所有訂單</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">尚無訂單</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-gray-700">訂單編號</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">客戶</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">產品</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-700">金額</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-700">狀態</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-700">時間</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-700">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.slice(0, 10).map((order) => (
                      <tr key={order.id} className="border-b hover:bg-gray-50/50 transition-colors">
                        <td className="py-3 px-4">
                          <span className="font-mono text-sm">{order.order_number}</span>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium">{order.recipient_name}</p>
                            <p className="text-sm text-gray-500 truncate max-w-[200px]">
                              {order.recipient_email}
                            </p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium truncate max-w-[200px]">{order.product_name}</p>
                            <p className="text-sm text-gray-500">x {order.quantity}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="font-semibold text-blue-600">
                            NT$ {order.total_amount.toLocaleString()}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center gap-2">
                            {getStatusIcon(order.payment_status)}
                            <span className="text-sm">
                              {getStatusText(order.payment_status)}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center text-sm text-gray-500">
                          {new Date(order.created_at).toLocaleDateString('zh-TW')}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => navigate(`/order-detail/${order.order_number}`)}
                              title="查看詳情"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setSelectedOrder(order);
                                setDialogOpen(true);
                              }}
                              title="更新狀態"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 訂單狀態更新對話框 */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>更新訂單狀態</DialogTitle>
              <DialogDescription>
                訂單編號: {selectedOrder?.order_number}
              </DialogDescription>
            </DialogHeader>
            {selectedOrder && (
              <OrderStatusManager 
                order={selectedOrder}
                onUpdate={() => {
                  setDialogOpen(false);
                  fetchData();
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default AdminDashboard;
